/**
 * BrandDomainService — Marken-Domain-Endpunkte über die http-Instanz
 * (`@Controller('brands/:brandKey/domains')`). Die http-Instanz entpackt
 * `response.data` bereits im Interceptor, daher geben die Methoden die Nutzdaten
 * direkt zurück. Der Server-Scope (admin global, brand-admin nur eigene Marke)
 * ist maßgeblich; dieser Client spiegelt nur die Endpunkte.
 */

import http from './http'
import type { BrandDomain, BrandDomainList, BrandDomainResult } from '../types'

/**
 * Listet die Domains einer Marke inkl. DNS-Anleitung je Domain.
 *
 * @param brandKey Schlüssel der Marke
 * @returns `{ domains, instructions }`
 */
async function list(brandKey: string): Promise<BrandDomainList> {
  return http.get(`/brands/${brandKey}/domains`)
}

/**
 * Fügt der Marke eine Domain hinzu (Status `pending`).
 *
 * @param brandKey Schlüssel der Marke
 * @param hostname FQDN der Domain
 * @returns `{ domain, instructions }`
 */
async function add(brandKey: string, hostname: string): Promise<BrandDomainResult> {
  return http.post(`/brands/${brandKey}/domains`, { hostname })
}

/**
 * Prüft den DNS-TXT-Record und schaltet die Domain bei Übereinstimmung automatisch frei.
 *
 * @param brandKey Schlüssel der Marke
 * @param id Domain-ID
 * @returns die aktualisierte Domain
 */
async function verify(brandKey: string, id: string): Promise<BrandDomain> {
  return http.post(`/brands/${brandKey}/domains/${id}/verify`)
}

/**
 * Versendet die DNS-Anleitung per E-Mail.
 *
 * @param brandKey Schlüssel der Marke
 * @param id Domain-ID
 * @param email Empfänger-Adresse
 * @returns `{ sent: true }`
 */
async function sendInstructions(
  brandKey: string,
  id: string,
  email: string,
): Promise<{ sent: true }> {
  return http.post(`/brands/${brandKey}/domains/${id}/send-instructions`, { email })
}

/**
 * Setzt die Domain als primäre Domain der Marke (exklusiv je Marke).
 *
 * @param brandKey Schlüssel der Marke
 * @param id Domain-ID
 * @returns die aktualisierte Domain
 */
async function setPrimary(brandKey: string, id: string): Promise<BrandDomain> {
  return http.put(`/brands/${brandKey}/domains/${id}/primary`)
}

/**
 * Entfernt eine Domain der Marke.
 *
 * @param brandKey Schlüssel der Marke
 * @param id Domain-ID
 * @returns `{ deleted: true }`
 */
async function remove(brandKey: string, id: string): Promise<{ deleted: true }> {
  return http.delete(`/brands/${brandKey}/domains/${id}`)
}

export const BrandDomainService = {
  list,
  add,
  verify,
  sendInstructions,
  setPrimary,
  remove,
}
